import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { ModalService } from '../../common/modal/modal-service';
import { form, FormField } from '@angular/forms/signals';
import { AttendingStatus } from '../../../models/AttendingStatus.enum';
import { RsvpData } from '../../../models/RsvpData';
import { InvitationType } from '../../../models/InvitationType.enum';
import { InvitationTypeIconPipe } from "../../../pipes/invitation-type-icon-pipe";
import { attendingIconPipe } from "../../../pipes/attending-icon-pipe-pipe";
import { ToastrService } from 'ngx-toastr';
import { UpdateService } from '../../../services/update-service';
import { RsvpService } from '../../../services/rsvp-service';
import { RsvpDataResponse } from '../../../dto/RsvpDataResponse';
import { Person } from '../../../models/Person';
import { v4 as uuid } from 'uuid';
import { AnalyseService } from '../../../services/analyse-service';

@Component({
  selector: 'app-update-family-rsvp',
  imports: [InvitationTypeIconPipe, FormField, attendingIconPipe],
  templateUrl: './update-family-rsvp.html',
  styleUrl: 'update-family-rsvp.scss',
})
export class UpdateFamilyRsvp implements OnInit {
  readonly modalService = inject(ModalService);
  private toastrService = inject(ToastrService);
  private updateService = inject(UpdateService);
  private rsvpService = inject(RsvpService);
    private analyseService = inject(AnalyseService);

  readonly familyUuid = input('');
  readonly confirmDeleteState = signal(false);
  isGenerating = signal(false);

  attendingStatusOptions = [
    AttendingStatus.NOT_RESPONDED,
    AttendingStatus.ATTENDING,
    AttendingStatus.NOT_ATTENDING,
    AttendingStatus.NOT_SURE_YET,
  ];

  invitationTypesOptions = [
    InvitationType.FAMILLE,
    InvitationType.AMIS,
    InvitationType.AMIS_PARENTS,
    InvitationType.VH,
    InvitationType.VH_PARENTS
  ];

  familyPrivateDataModel = signal({
    canInviteChildren: false,
    canInviteCouple: false,
    invitationType: InvitationType.EMPTY.getKey(),
    departement: '34'
  });

  rsvpDataModel = signal<RsvpData>({
    person1: { firstName: "", lastName: "", adult: true, personId: '' },
    person2: undefined,
    childs: [],
    attendingStatus: AttendingStatus.NOT_RESPONDED.getKey(),
    comment: '',
    bonusQuestionAnswer: '',
    staysForBrunch: false,
  });

  familyPrivateDataForm = form(this.familyPrivateDataModel);
  rsvpDataForm = form(this.rsvpDataModel);

  buttonConfimDisabled = computed(
    () => this.familyPrivateDataModel().invitationType === InvitationType.EMPTY.getKey()
      || this.rsvpDataModel().person1.lastName === '' || this.rsvpDataModel().person1.firstName === ''
      || (this.familyPrivateDataModel().departement.length !== 2 && this.familyPrivateDataModel().departement.length !== 3)
      || !this.isFormDirty()
  );

  isFormDirty = computed(() => {
    return this.rsvpDataForm().dirty() || this.familyPrivateDataForm().dirty();
  });

   constructor() {
    effect(() => {
      if (!this.familyPrivateDataModel().canInviteCouple) {
        this.removeAdult('person2');
      };
    });
  }

  ngOnInit(): void {
    this.rsvpService.getRsvpData(this.familyUuid()).then(
      response => {
        this.updateFormData(response);

        this.rsvpDataForm().reset();
        this.familyPrivateDataForm().reset();
      }).catch(() => {
        this.toastrService.error('Erreur de recuperation des données');
        this.modalService.discardModal(false);
      }
      );
  }

  updateFormData(response: RsvpDataResponse): void {
    let person1: Person;
    let person2: Person | undefined;

    let childs: Person[] = [];

    response.persons.forEach((person: Person) => {
      if (person.adult && !person1) {
        person1 = person;
      } else if (person.adult && !person2) {
        person2 = person;
      } else {
        childs.push(person);
      }
    });

    this.familyPrivateDataModel.set({
      canInviteChildren: response.canRequireChildsInvite,
      canInviteCouple: response.canRequireCoupleInvite,
      invitationType: response.invitationType,
      departement: response.familyDepartement
    });

    this.rsvpDataModel.set({
      person1: { ...person1!, lastName: person1!.lastName.toUpperCase() },
      person2: person2 ? { ...person2, lastName: person2!.lastName.toUpperCase() } : undefined,
      childs: childs?.map(child => ({ ...child, lastName: child.lastName.toUpperCase() })),
      attendingStatus: response.attendingStatus,
      comment: response.comment,
      bonusQuestionAnswer: response.bonusQuestionAnswer,
      staysForBrunch: response.staysForBrunch
    });
  }

  addChild() {
    this.rsvpDataModel.update(data => ({
      ...data,
      childs: [...data.childs!, { firstName: "", lastName: "", adult: false, personId: uuid().toString() }],
    }));
    this.rsvpDataForm().markAsDirty();
  }

  removeChild(index: number) {
    this.rsvpDataModel.update(data => ({
      ...data,
      childs: data.childs!.filter((_, i) => i !== index),
    }));
    this.rsvpDataForm().markAsDirty();
  }

  removeAdult(key: 'person1' | 'person2') {
    if (key === 'person1') {
      this.rsvpDataModel.update(data => ({
        ...data,
        ['person1']: this.rsvpDataModel().person2!,
      }));
    }

    this.rsvpDataModel.update(data => ({
      ...data,
      ['person2']: undefined,
    }));
    this.rsvpDataForm().markAsDirty();
  }

  addAdult() {
    this.rsvpDataModel.update(data => ({
      ...data,
      ['person2']: { firstName: "", lastName: "", adult: true, personId: uuid().toString() },
    }));
    this.rsvpDataForm().markAsDirty();
  }

  validate(): void {
    if (!this.buttonConfimDisabled()) {
      this.updateService.updateFamily(this.familyUuid(), this.rsvpDataModel(), this.familyPrivateDataModel())
        .then(val => {
          if (val) {
            this.toastrService.success('Famille sauvegardée');
            this.modalService.shouldClose.set(true);
          } else {
            this.toastrService.error('Erreur de mise à jour');
            this.modalService.discardModal(false);
          }
        })
        .catch(() => {
          this.toastrService.error('Erreur de mise à jour');
          this.modalService.discardModal(false);
        });
    }

  }

  cancel(): void {
    if (!this.buttonConfimDisabled()) {
      this.toastrService.info('Annulation des modifications');
    }
    this.modalService.discardModal(false);
  }

  askForDelete(): void {
    this.confirmDeleteState.set(true);
  }

  confirmDelete(): void {
    this.updateService.uninviteFamily(this.familyUuid()).then(val => {
      if (val) {
        this.toastrService.success('Famille supprimée');
        this.modalService.shouldClose.set(true);
      } else {
        this.toastrService.error('Erreur de supression');
        this.modalService.discardModal(false);
      }
    })
    .catch(() => {
      this.toastrService.error('Erreur de supression');
      this.modalService.discardModal(false);
    });
  }

  cancelDelete(): void {
    this.confirmDeleteState.set(false);
  }

  async downloadSpecificInvitation(): Promise<void> {
    this.isGenerating.set(true);
    try {
      const blob = await this.analyseService.getSpecificInvitations([this.familyUuid()]);

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'invitations_specific.pdf';
      a.click();
      URL.revokeObjectURL(url);

    } finally {
      this.isGenerating.set(false);
    }
  }
}
